/**
 * Configuración de entorno del **portal del cliente**, validada y tipada.
 *
 * Todo el código consume `env` en lugar de `import.meta.env` directamente: los
 * valores por defecto y las validaciones viven en un solo sitio y los errores de
 * configuración aparecen al arrancar, no en runtime.
 */

function readString(key: keyof ImportMetaEnv, fallback: string): string {
  const raw = import.meta.env[key];
  if (typeof raw !== 'string' || raw.trim() === '') return fallback;
  return raw.trim();
}

function readInt(key: keyof ImportMetaEnv, fallback: number): number {
  const parsed = Number.parseInt(readString(key, String(fallback)), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function readUrl(key: keyof ImportMetaEnv, fallback: string): string {
  const value = readString(key, fallback).replace(/\/+$/, '');

  try {
    void new URL(value);
  } catch {
    const destino = fallback === '' ? 'el mismo origen' : fallback;
    console.warn(`[env] ${key} no es una URL válida ("${value}"). Se usa ${destino}.`);
    return fallback.replace(/\/+$/, '');
  }

  return value;
}

/**
 * URL base de la API.
 *
 * - Valor con esquema (`https://api...`): el portal llama directo a ese host.
 * - Cadena vacía: mismo origen; el proxy de Vite (`server.proxy`) en desarrollo y
 *   el de `server.js` en producción reenvían `/api` a EdSuite API. Es el modo
 *   recomendado: sin CORS y compatible con entornos remotos.
 * - Sin definir (build ejecutado sin `.env`): también mismo origen.
 *
 * Nunca se cae a `http://localhost:9000`: el bundle lo ejecuta el navegador de
 * cada cliente, así que esa URL apuntaría al **equipo del visitante** (donde no
 * hay API) en lugar del servidor. Si se compila sin `.env`, el «mismo origen»
 * sigue funcionando gracias al proxy de `server.js`.
 */
function readApiUrl(): string {
  const raw = import.meta.env.VITE_API_URL;

  if (raw === undefined) return '';

  const value = raw.trim().replace(/\/+$/, '');
  if (value === '') return '';

  return readUrl('VITE_API_URL', '');
}

/**
 * Prefijo bajo el que se sirve el portal.
 *
 * Cadena vacía = raíz del dominio (`https://portal.escienza.com.pe/resumen`).
 * Con `/portal/` las mismas rutas cuelgan de ese subdirectorio.
 */
function readBasePath(): string {
  const raw = (import.meta.env.VITE_BASE_PATH ?? '').trim();

  if (raw === '' || raw === '/') return '';

  const conBarraInicial = raw.startsWith('/') ? raw : `/${raw}`;

  return conBarraInicial.replace(/\/+$/, '');
}

export const env = {
  /**
   * URL base de EdSuite API.
   * Cadena vacía = mismo origen (proxy de Vite / `server.js`).
   */
  apiUrl: readApiUrl(),

  /** Prefijo de despliegue del portal (vacío = raíz del dominio). */
  basePath: readBasePath(),

  /** Nombre comercial mostrado en la interfaz. */
  appName: readString('VITE_APP_NAME', 'EDSuite'),

  /**
   * URL del **panel** (el otro acceso de EDSuite).
   *
   * El portal es una aplicación aparte, así que un operador que entre aquí por
   * error necesita la dirección del panel. Vacío = no se ofrece el enlace (es lo
   * recomendable cuando el portal está en un subdominio público).
   */
  panelUrl: readString('VITE_PANEL_URL', '').replace(/\/+$/, ''),

  /** Timeout por defecto de cada request (ms). */
  apiTimeout: readInt('VITE_API_TIMEOUT', 20_000),

  /** Atajos de entorno. */
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
  mode: import.meta.env.MODE,
} as const;

export type AppEnv = typeof env;
